/**
 * @file Monde d'entites : creation/destruction differee, ids stables croissants, index par type, hook onDestroy (grille, pool de projectiles).
 */
import type { EntityKind } from '@shared/enums';
import type { EntityId } from '@shared/types';
import type { Ai, Cat, Collider, Health, Mover, Projectile, Reward, Target, Transform, Visual } from './components';

/** Entite = identifiant + sac de composants optionnels (struct-of-optionals, typage strict par composant). */
export interface Entity {
  readonly id: EntityId;
  kind: EntityKind;
  alive: boolean;
  transform: Transform;
  health?: Health;
  reward?: Reward;
  mover?: Mover;
  ai?: Ai;
  collider?: Collider;
  target?: Target;
  cat?: Cat;
  projectile?: Projectile;
  visual?: Visual;
}

/** Entite garantie de posseder certains composants (resultat de `query`). */
export type With<K extends keyof Entity> = Entity & Required<Pick<Entity, K>>;

export const hasComps = <K extends keyof Entity>(e: Entity, ...keys: K[]): e is With<K> => {
  for (const k of keys) if (e[k] === undefined) return false;
  return true;
};

export class World {
  private nextId: EntityId = 1;
  private readonly entities = new Map<EntityId, Entity>();
  private readonly byKind: Record<EntityKind, Set<EntityId>> = {
    cat: new Set(),
    target: new Set(),
    projectile: new Set(),
    pickup: new Set(),
    fx: new Set(),
  };
  private readonly pendingDestroy: EntityId[] = [];
  /** Hook appele juste avant la suppression definitive (grille spatiale, pool...). */
  onDestroy: ((e: Entity) => void) | null = null;

  create(kind: EntityKind, x = 0, y = 0): Entity {
    const id = this.nextId++;
    const e: Entity = { id, kind, alive: true, transform: { x, y, prevX: x, prevY: y, rot: 0 } };
    this.entities.set(id, e);
    this.byKind[kind].add(id);
    return e;
  }

  get(id: EntityId): Entity | undefined {
    return this.entities.get(id);
  }

  /** Marque l'entite morte ; suppression effective au prochain `flush()` (fin de tic) pour garder l'iteration stable. */
  destroy(id: EntityId): void {
    const e = this.entities.get(id);
    if (!e || !e.alive) return;
    e.alive = false;
    this.pendingDestroy.push(id);
  }

  flush(): void {
    for (const id of this.pendingDestroy) {
      const e = this.entities.get(id);
      if (!e) continue;
      this.onDestroy?.(e);
      this.entities.delete(id);
      this.byKind[e.kind].delete(id);
    }
    this.pendingDestroy.length = 0;
  }

  /** Iteration deterministe (ordre d'insertion = id croissant) sur les entites vivantes d'un type. */
  *ofKind(kind: EntityKind): IterableIterator<Entity> {
    for (const id of this.byKind[kind]) {
      const e = this.entities.get(id);
      if (e && e.alive) yield e;
    }
  }

  /** Liste des entites vivantes d'un type possedant les composants demandes. */
  query<K extends keyof Entity>(kind: EntityKind, ...keys: K[]): Array<With<K>> {
    const out: Array<With<K>> = [];
    for (const e of this.ofKind(kind)) if (hasComps(e, ...keys)) out.push(e);
    return out;
  }

  count(kind: EntityKind): number {
    let n = 0;
    for (const _ of this.ofKind(kind)) n++;
    return n;
  }

  /** Detruit toutes les entites d'un type (changement de mode). */
  clearKind(kind: EntityKind): void {
    for (const id of this.byKind[kind]) this.destroy(id);
    this.flush();
  }

  clearAll(): void {
    for (const id of this.entities.keys()) this.destroy(id);
    this.flush();
  }

  /** Toutes les entites (lecture seule pour la vue du rendu). */
  all(): IterableIterator<Entity> {
    return this.entities.values();
  }
}
